import { Injectable } from '@angular/core';
import { CanActivate, CanActivateChild, Resolve } from '@angular/router';

import { catchError, map, Observable, of, shareReplay, tap } from 'rxjs';

import { ApiClientService } from '@tablesready/api-client';
import { AppVariablesResponse } from '@shared/public/models';
import { AlertsService, IAlertTypes } from '@tablesready/frontend-alerts-bar';
import { AppVariables, AppVariablesBrand } from './app-variables.interfaces';

const DEFAULT_FULLCALENDAR_KEY = 'CC-Attribution-NonCommercial-NoDerivatives';

const DEFAULT_VARS: AppVariables = {
  WHITELABEL_BRAND: AppVariablesBrand.TablesReady,
  WHITELABEL_LOGO: 'tr_logo_secondary_300_dark.png',
  WHITELABEL_HOSTNAME: 'https://host.tablesready.com',
  WHITELABEL_LINK: 'www.tablesready.com',
  WHITELABEL_SMS_CHARS: '320',
  WHITELABEL_SMS_NON_ANSI_CHARS: '140',
  ACCOUNT_DELETING_DELAY: '48 hours',
  FREE_PLAN_LOCATION_LIMIT: 2,
  MAX_SAVED_REPLIES: 10,
  FULL_CALENDAR_KEY: DEFAULT_FULLCALENDAR_KEY,
  UNPAID_PARTY_LIFETIME: 600,
  SERVICE_FEE: 2,
  APP_VERSION: Date.now().toString(),
};

@Injectable()
export class AppVariablesService implements
    Resolve<ReturnType<AppVariablesService['getAppVariables']>>,
    CanActivate,
    CanActivateChild {
  readonly appVariables: Observable<AppVariables>;
  private _appVariables: AppVariables | null = null;

  constructor(
    private readonly http: ApiClientService,
    private readonly alertsService: AlertsService,
  ) {
    this.appVariables = this.getAppVariables().pipe(shareReplay(1));
  }

  getAppVariables(): Observable<AppVariables> {
    return this.http
      .get<AppVariablesResponse>('/appvariables')
      .pipe(
        map(({ success, data }): AppVariables => {
          if (success) {
            return {
              WHITELABEL_BRAND: data.WHITELABEL_BRAND as AppVariablesBrand,
              WHITELABEL_LINK: data.WHITELABEL_LINK,
              WHITELABEL_LOGO: data.WHITELABEL_LOGO,
              WHITELABEL_HOSTNAME: data.WHITELABEL_HOSTNAME,
              WHITELABEL_SMS_CHARS: data.WHITELABEL_SMS_CHARS,
              WHITELABEL_SMS_NON_ANSI_CHARS: data.WHITELABEL_SMS_NON_ANSI_CHARS,
              ACCOUNT_DELETING_DELAY: data.ACCOUNT_DELETING_DELAY,
              FREE_PLAN_LOCATION_LIMIT: data.FREE_PLAN_LOCATION_LIMIT,
              MAX_SAVED_REPLIES: data.MAX_SAVED_REPLIES,
              FULL_CALENDAR_KEY: data.FULL_CALENDAR_KEY,
              UNPAID_PARTY_LIFETIME: data.UNPAID_PARTY_LIFETIME,
              SERVICE_FEE: data.SERVICE_FEE,
              APP_VERSION: data.APP_VERSION,
            };
          }
          throw new Error();
        }),
        catchError(() => of(DEFAULT_VARS)),
        tap((appVariables) => {
          this._appVariables = appVariables;

          const scriptTags = document.querySelectorAll('script');
          const versionPattern = /main\.(\d+\.\d+\.\d+)\.js/;
          let version = null;

          scriptTags.forEach((script) => {
            const match = script.src.match(versionPattern);
            if (match) {
              version = match[1];
            }
          });

          if (version && version !== appVariables.APP_VERSION) {
            this.alertsService.add({
              id: 'app-out-of-date',
              type: IAlertTypes.warning,
              duration: 7 * 24 * 60 * 60 * 1000,
              time: Date.now(),
              heading: '',
              body: 'Your app version is out of date. Click here to reload the page link. If this doesn\'t work, please clear your browser cache.',
              actions: [
                {
                  label: 'Click here to reload.',
                  action: (): void => {
                    window.location.reload();
                  },
                },
              ],
            });
          }
        }),
      );
  }

  resolve() {
    return this.appVariables;
  }

  canActivateChild() {
    return this.appVariables.pipe(map(() => true));
  }

  canActivate() {
    return this.appVariables.pipe(map(() => true));
  }

  get() {
    return this._appVariables;
  }
}
